import React, { useState, useEffect } from 'react';
import {
  ChakraProvider,
  Box,
  Container,
  VStack,
  SimpleGrid,
  Heading,
  Text,
  useToast,
  HStack,
  Link,
} from '@chakra-ui/react';
import theme from './theme';
import './App.css';
import AuthButtons from './components/AuthButtons';
import TopNav from './components/TopNav';
import Cases from './pages/Cases';
import Reports from './pages/Reports';
import Roles from './pages/Roles';
import Users from './pages/Users';

function App() {
  const toast = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPage, setCurrentPage] = useState('cases');
  const [userRole, setUserRole] = useState(null);
  
  // Handle navigation
  const handleNavClick = (page) => {
    setCurrentPage(page);
  };

  // Handle role change from TopNav
  const handleRoleChange = (role) => {
    setUserRole(role);
  };

  // Handle redirect when user becomes unauthenticated on a protected page
  const handleNotAuthenticated = () => {
    setIsAuthenticated(false);
    setCurrentPage('cases');
  };

  // Render the appropriate page based on currentPage state
  const renderPage = () => {
    if (!isAuthenticated) {
      return null;
    }

    switch (currentPage) {
      case 'cases':
        return <Cases userRole={userRole} isAuthenticated={isAuthenticated} onNotAuthenticated={handleNotAuthenticated} />;
      case 'reports':
        return <Reports isAuthenticated={isAuthenticated} onNotAuthenticated={handleNotAuthenticated} />;
      case 'roles':
        return <Roles isAuthenticated={isAuthenticated} onNotAuthenticated={handleNotAuthenticated} />;
      case 'users':
        return <Users isAuthenticated={isAuthenticated} onNotAuthenticated={handleNotAuthenticated} />;
      case 'home':
      default:
        return (
          <Container 
            maxW={{ base: "sm", md: "lg", lg: "2xl" }} 
            centerContent 
            h={isAuthenticated ? "auto" : "100vh"}
            display="flex" 
            flexDirection="column" 
            justifyContent={isAuthenticated ? "flex-start" : "center"}
            px={{ base: 4, md: 6 }}
            pt={isAuthenticated ? { base: 16, md: 4 } : 0}
          >
            <VStack spacing={8} width="100%" maxW="100%">
              {/* Header Section */}
              <VStack spacing={2} textAlign="center" width="100%">
                <Heading
                  as="h1"
                  size={{ base: "xl", md: "2xl", lg: "3xl" }}
                  bgGradient="linear(to-r, blue.600, blue.400)"
                  bgClip="text"
                >
                  Renest Reporter
                </Heading>
               
              </VStack>

              {/* Authenticated Content */}
              <Box
                width="100%"
                p={{ base: 4, md: 6 }}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                textAlign="center"
              >
                <Heading as="h3" size={{ base: "md", md: "lg" }} mb={2} color="green.800">
                  Welcome!
                </Heading>
                <Text fontSize={{ base: "sm", md: "md" }} color="gray.700">
                  Select "Cases" from the menu to manage wildlife observation cases.
                </Text>
              </Box>

              {/* Footer Links */}
              <HStack spacing={4} fontSize="sm" color="gray.600" pt={8} justifyContent="center">
                <Link href="/privacy-policy.html" isExternal _hover={{ color: 'blue.600' }}>
                  Privacy Policy
                </Link>
                <Text>•</Text>
                <Link href="/terms-of-service.html" isExternal _hover={{ color: 'blue.600' }}>
                  Terms of Service
                </Link>
              </HStack>
            </VStack>
          </Container>
        );
    }
  };
  
  // Check authentication status on component mount
  useEffect(() => {
    // Check both local storage and backend session
    checkAuthStatus();
    checkBackendAuthStatus();
    
    // Add event listener for messages from OAuth popup
    const handleAuthMessage = (event) => {
      if (!event.data || !event.data.type) return;
      
      switch (event.data.type) {
        case 'AUTH_SUCCESS':
          handleAuthSuccess();
          break;
          
        case 'CHECK_BACKEND_AUTH':
          // Give the backend a moment to save the session
          setTimeout(() => {
            checkBackendAuthStatus();
          }, 500);
          break;
          
        case 'FINAL_CHECK':
          // Do a final auth check to ensure UI is up to date
          checkBackendAuthStatus();
          checkAuthStatus();
          break;
          
        default:
          // Ignore unknown message types
          break;
      }
    };
    
    window.addEventListener('message', handleAuthMessage);
    
    // Set up periodic auth check (every 60 seconds)
    const authCheckInterval = setInterval(() => {
      checkAuthStatus();
      checkBackendAuthStatus();
    }, 60000);
    
    // Clean up event listeners and intervals on unmount
    return () => {
      window.removeEventListener('message', handleAuthMessage);
      clearInterval(authCheckInterval);
    };
  }, []);
  
  // Check authentication status with the backend
  const checkBackendAuthStatus = () => {
    // Get the base API URL from environment variables
    const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
    
    // Use relative path if same origin, otherwise use full URL
    const backendUrl = window.location.origin.includes(baseApiUrl) ? 
      '/auth-status.php' :  // Same origin
      `${baseApiUrl}/auth-status.php`;  // Cross-origin
    
    fetch(backendUrl, {
      credentials: 'include', // Important for cookies/sessions
      // Simplified headers to avoid CORS issues
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      
      if (data.isAuthenticated) {
        setIsAuthenticated(true);
        toast({
          title: '✓ Authenticated!',
          description: 'Successfully logged in with Google.',
          status: 'success',
          duration: 4000,
          isClosable: true,
          position: 'top-right',
        });
        
        // Fetch user email
        fetchUserEmail();
      } else {
        // Check localStorage as fallback
        const token = getStoredToken();
        if (token && new Date(token.expires_at) > new Date()) {
          setIsAuthenticated(true);
          // Fetch user email
          fetchUserEmail();
        }
      }
    })
    .catch(error => {
      // Fallback to localStorage check if backend check fails
      const token = getStoredToken();
      if (token && new Date(token.expires_at) > new Date()) {
        setIsAuthenticated(true);
      }
    });
  };

  // Check if the user is already authenticated
  const checkAuthStatus = () => {
    const token = getStoredToken();
    if (token && new Date(token.expires_at) > new Date()) {
      // Token exists and is not expired
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  };

  // Get token from localStorage
  const getStoredToken = () => {
    const tokenStr = localStorage.getItem('google_token');
    if (!tokenStr) return null;
    try {
      return JSON.parse(tokenStr);
    } catch (e) {
      localStorage.removeItem('google_token');
      return null;
    }
  };

  // Fetch user email from backend
  const fetchUserEmail = () => {
    const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
    
    const userEmailUrl = window.location.origin.includes(baseApiUrl) ? 
      '/user-email.php' :  // Same origin
      `${baseApiUrl}/user-email.php`;  // Cross-origin
    
    // Get token from localStorage
    const tokenStr = localStorage.getItem('google_token');
    const token = tokenStr ? JSON.parse(tokenStr) : null;
    const accessToken = token?.access_token;
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add Authorization header if we have a token
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    fetch(userEmailUrl, {
      credentials: 'include',
      headers: headers
    })
    .then(response => {
      return response.json().then(data => ({
        status: response.status,
        ok: response.ok,
        data: data
      }));
    })
    .then(result => {
      if (!result.ok) {
        console.error('Error response:', result.status, result.data);
        return;
      }
      console.log('User Email:', result.data.email);
      console.log('Full response:', result.data);
    })
    .catch(error => {
      console.error('Error fetching user email:', error);
    });
  };

  // Handle successful authentication
  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    toast({
      title: '🔐 Sign In Successful!',
      description: 'You have been authenticated with Google.',
      status: 'success',
      duration: 4000,
      isClosable: true,
      position: 'top-right',
    });
    checkAuthStatus();
  };

  // Handle sign out
  const handleSignOut = () => {
    localStorage.removeItem('google_token');
    setIsAuthenticated(false);
    setCurrentPage('cases'); // Reset to cases page
    toast({
      title: 'Signed Out',
      description: 'You have been logged out successfully.',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  return (
    <ChakraProvider theme={theme}>
      <TopNav 
        isAuthenticated={isAuthenticated} 
        onNavClick={handleNavClick} 
        onSignOut={handleSignOut} 
        onRoleChange={handleRoleChange}
        currentPage={currentPage}
      />
      
      <Box 
        minH="100vh" 
        bgGradient="linear(to-br, blue.50, blue.100)"
      >
        {/* Show auth buttons on login page */}
        {!isAuthenticated && (
          <Container 
            maxW={{ base: "sm", md: "2xl", lg: "4xl" }} 
            centerContent 
            h="100vh"
            display="flex" 
            flexDirection="column" 
            justifyContent="center"
            px={{ base: 4, md: 6 }}
          >
            <VStack spacing={10} width="100%" maxW="100%">
              {/* Header Section */}
              <VStack spacing={4} textAlign="center" width="100%">
                <Heading
                  as="h1"
                  size={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  bgGradient="linear(to-r, blue.600, blue.400)"
                  bgClip="text"
                  fontWeight="bold"
                >
                  🦅 Renest Reporter
                </Heading>
                <Text fontSize={{ base: "sm", md: "md" }} color="gray.700" maxW="100%">
                  Wildlife Case Management & Monitoring System
                </Text>
              </VStack>

              {/* Main Content Grid */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} width="100%">
                {/* Left: Description */}
                <VStack spacing={6} justify="flex-start">
                  <Box>
                    <Heading 
                      as="h2" 
                      size={{ base: "lg", md: "xl" }} 
                      mb={4}
                      color="blue.800"
                    >
                      What is Renest Reporter?
                    </Heading>
                    <VStack spacing={4} align="start">
                      <Box>
                        <Heading as="h4" size="sm" mb={1} color="blue.700">
                          📋 Case Management
                        </Heading>
                        <Text fontSize={{ base: "sm", md: "md" }} color="gray.700">
                          Track and manage wildlife observation cases with detailed information about species, locations, and dates.
                        </Text>
                      </Box>
                      
                      <Box>
                        <Heading as="h4" size="sm" mb={1} color="blue.700">
                          🔍 Nest Investigation
                        </Heading>
                        <Text fontSize={{ base: "sm", md: "md" }} color="gray.700">
                          Record investigation findings and conclusions to support rescue and renesting efforts.
                        </Text>
                      </Box>
                      
                      <Box>
                        <Heading as="h4" size="sm" mb={1} color="blue.700">
                          📊 Reporting & Analytics
                        </Heading>
                        <Text fontSize={{ base: "sm", md: "md" }} color="gray.700">
                          Generate comprehensive reports on case outcomes and program effectiveness.
                        </Text>
                      </Box>
                      
                      <Box>
                        <Heading as="h4" size="sm" mb={1} color="blue.700">
                          👥 Role-Based Access
                        </Heading>
                        <Text fontSize={{ base: "sm", md: "md" }} color="gray.700">
                          Manage user permissions for staff, volunteers, and administrators.
                        </Text>
                      </Box>
                    </VStack>
                  </Box>
                </VStack>

                {/* Right: Login Section */}
                <VStack spacing={6} justify="center">
                  <Box
                    width="100%"
                    p={{ base: 6, md: 8 }}
                    bg="white"
                    borderRadius="xl"
                    boxShadow="lg"
                    border="1px"
                    borderColor="gray.200"
                  >
                    <VStack spacing={6}>
                      <VStack spacing={2} width="100%">
                        <Heading 
                          as="h3" 
                          size={{ base: "md", md: "lg" }} 
                          color="blue.800"
                        >
                          Get Started
                        </Heading>
                        <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" textAlign="center">
                          Sign in with your Google account to access the system
                        </Text>
                      </VStack>

                      {/* Auth Buttons */}
                      <Box width="100%">
                        <AuthButtons
                          isAuthenticated={isAuthenticated}
                          onAuthSuccess={handleAuthSuccess}
                          onSignOut={handleSignOut}
                        />
                      </Box>

                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        We securely store only your email address for authentication
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
              </SimpleGrid>

              {/* Footer Links */}
              <HStack 
                spacing={4} 
                fontSize="sm" 
                color="gray.600" 
                pt={4} 
                justifyContent="center"
                flexWrap="wrap"
              >
                <Link href="/privacy-policy.html" isExternal _hover={{ color: 'blue.600' }}>
                  Privacy Policy
                </Link>
                <Text>•</Text>
                <Link href="/terms-of-service.html" isExternal _hover={{ color: 'blue.600' }}>
                  Terms of Service
                </Link>
              </HStack>
            </VStack>
          </Container>
        )}

        {/* Show page content when authenticated */}
        {isAuthenticated && renderPage()}
      </Box>
    </ChakraProvider>
  );
}

export default App;